/**
 * Registering a directory as reachable — which is where the confinement
 * boundary is actually set.
 *
 * Everything else about this surface is downstream of what happens in this
 * dialog. A path filtered at read time cannot be made safe, because the
 * transport arrives as root and a hard link gives the same bytes a second name
 * that carries no target for any resolver to follow. What *can* be made safe is
 * a directory somebody chose on purpose, once, with a reason recorded beside it.
 *
 * So the refusal an operator meets here is not a validation nicety. It is the
 * control, and the server writes it as a denial in the audit trail rather than
 * as a bad request.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'

import { ApiError, api, type PageResponse } from '@/api/client'
import { Icon } from '@/components/icon'
import { Badge, Button, EmptyState } from '@/components/primitives'
import { CheckboxField, Dialog, Notice, SelectField, TextField, useApiErrorMessage } from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useT } from '@/i18n/context'
import type { ID } from '@/types/domain'
import { createRoot, listRoots, removeRoot, type HostRoot } from './api'

interface HostLite {
  id: ID
  name: string
}

/**
 * Why a directory was refused, in the reader's language.
 *
 * The server sends a *category* and a direction, never the colliding path — a
 * provider profile directory is deliberately kept off the wire, and a refusal
 * that printed one would put it there by the back door. So the sentence is
 * composed here, which is also the console's standing rule: interface text comes
 * from the translation table, and a sentence assembled on a machine that does
 * not know who is reading cannot obey it. The first version of this endpoint
 * sent English prose and an operator met it in a Turkish console.
 *
 * The two directions get different sentences because they have different fixes.
 * "This contains credentials" is solved by registering something narrower;
 * "this is inside credentials" is not solved by narrowing at all, and telling
 * somebody to narrow would send them round a loop.
 */
function refusalSentence(err: unknown, t: ReturnType<typeof useT>): string {
  if (!(err instanceof ApiError)) return ''
  const holds = err.fields?.holds
  if (!holds) return ''
  const what =
    holds === 'account'
      ? t('hostfiles.holds.account')
      : holds === 'keys'
        ? t('hostfiles.holds.keys')
        : holds === 'env'
          ? t('hostfiles.holds.env')
          : holds === 'ssh'
            ? t('hostfiles.holds.ssh')
            : t('hostfiles.holds.state')
  return err.fields?.inside === 'yes'
    ? t('hostfiles.refusedInside', { what })
    : t('hostfiles.refusedContains', { what })
}

export function HostRootsDialog({ onClose }: { onClose: () => void }) {
  const t = useT()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const queryClient = useQueryClient()

  const roots = useQuery({ queryKey: ['host-roots'], queryFn: listRoots })
  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<HostLite>>('/hosts'),
  })

  const [hostId, setHostId] = useState<ID>('')
  const [path, setPath] = useState('')
  const [label, setLabel] = useState('')
  // Off by default, and the label says why: taking something off a machine is
  // far more common than putting something on it, and a root that cannot be
  // written to cannot be used to plant anything.
  const [writable, setWritable] = useState(false)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['host-roots'] })

  const add = useMutation({
    mutationFn: () => createRoot({ hostId, path: path.trim(), label: label.trim(), writable }),
    onSuccess: () => {
      setPath('')
      setLabel('')
      setWritable(false)
      refresh()
    },
  })

  const drop = useMutation({
    mutationFn: (id: ID) => removeRoot(id),
    onSuccess: refresh,
  })

  const askRemove = async (root: HostRoot) => {
    const ok = await confirm({
      title: t('hostfiles.rootRemove'),
      message: t('hostfiles.rootRemoveConfirm'),
      confirmLabel: t('hostfiles.rootRemove'),
      tone: 'danger',
    })
    if (ok) drop.mutate(root.id)
  }

  const items = roots.data?.items ?? []

  return (
    <Dialog title={t('hostfiles.roots')} onClose={onClose}>
      <div className="k-fm__rootform">
        <SelectField
          label={t('hostfiles.rootHost')}
          value={hostId}
          onChange={setHostId}
          options={[
            { value: '', label: '—' },
            ...(hosts.data?.items ?? []).map((h) => ({ value: h.id, label: h.name })),
          ]}
        />
        <TextField
          label={t('hostfiles.rootPath')}
          hint={t('hostfiles.rootPathHint')}
          value={path}
          onChange={setPath}
          placeholder="/opt/kimene"
        />
        <TextField label={t('hostfiles.rootLabel')} value={label} onChange={setLabel} />
        <CheckboxField
          label={t('hostfiles.rootWritable')}
          hint={t('hostfiles.rootWritableHint')}
          checked={writable}
          onChange={setWritable}
        />
        {add.error != null && <Notice tone="err">{refusalSentence(add.error, t) || describe(add.error).title}</Notice>}
        <Button
          variant="primary"
          busy={add.isPending}
          disabled={hostId === '' || path.trim() === ''}
          onClick={() => add.mutate()}
        >
          <Icon icon={Plus} size={14} />
          {t('hostfiles.addRoot')}
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t('hostfiles.noRoots')} hint={t('hostfiles.noRootsHint')} />
      ) : (
        <ul className="k-fm__rootlist">
          {items.map((r) => (
            <li key={r.id} className="k-fm__rootrow">
              <span className="k-mono">{r.path}</span>
              <span className="k-fm__sub">{r.label}</span>
              <Badge tone="neutral" subtle>
                {r.hostName ?? r.hostId}
              </Badge>
              {!r.writable && (
                <Badge tone="neutral" subtle>
                  {t('hostfiles.readOnlyRoot')}
                </Badge>
              )}
              <span className="k-fm__spacer" />
              <Button size="sm" busy={drop.isPending} onClick={() => void askRemove(r)}>
                <Icon icon={Trash2} size={13} />
                {t('hostfiles.rootRemove')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}
